/**
 * Google Tag Manager utility functions for tracking user interactions
 */

// Initialize dataLayer if it doesn't exist
window.dataLayer = window.dataLayer || [];

/**
 * Push event to Google Tag Manager dataLayer
 * @param {string} event - Event name
 * @param {object} data - Additional event data
 */
export const trackEvent = (event, data = {}) => {
  try {
    window.dataLayer.push({
      event,
      ...data
    });
    console.log('GTM Event:', event, data);
  } catch (error) {
    console.warn('GTM tracking failed:', error);
  }
};

/**
 * Track page view
 * @param {string} page_title - Page title
 * @param {string} page_location - Page URL
 * @param {string} page_path - Page path
 */
export const trackPageView = (page_title, page_location, page_path) => {
  trackEvent('page_view', {
    page_title,
    page_location,
    page_path,
    timestamp: new Date().toISOString()
  });
};

/**
 * Track chess move actions
 * @param {string} action - Move action type (move, capture, castle, check, etc.)
 * @param {string} opening_name - Current opening name
 * @param {string} line_name - Current line name
 * @param {number} move_number - Move number in sequence
 * @param {string} move_notation - Chess notation (e.g., "e4", "Nf3")
 * @param {boolean} is_correct - Whether the move was correct
 */
export const trackChessMove = (action, opening_name, line_name, move_number, move_notation, is_correct) => {
  trackEvent('chess_move', {
    action,
    opening_name,
    line_name,
    move_number,
    move_notation,
    is_correct,
    timestamp: new Date().toISOString()
  });
};

/**
 * Track opening selection
 * @param {string} opening_name - Selected opening name
 * @param {string} player_color - Player color (white/black)
 * @param {number} lines_count - Number of lines in opening
 */
export const trackOpeningSelection = (opening_name, player_color, lines_count) => {
  trackEvent('opening_selected', {
    opening_name,
    player_color,
    lines_count,
    timestamp: new Date().toISOString()
  });
};

/**
 * Track line selection
 * @param {string} opening_name - Opening name
 * @param {string} line_name - Selected line name
 * @param {number} moves_count - Number of moves in line
 */
export const trackLineSelection = (opening_name, line_name, moves_count) => {
  trackEvent('line_selected', {
    opening_name,
    line_name,
    moves_count,
    timestamp: new Date().toISOString()
  });
};

/**
 * Track game completion
 * @param {string} opening_name - Opening name
 * @param {string} line_name - Line name
 * @param {number} total_moves - Total moves completed
 * @param {number} correct_moves - Number of correct moves
 * @param {number} duration_seconds - Time taken to complete
 */
export const trackGameCompletion = (opening_name, line_name, total_moves, correct_moves, duration_seconds) => {
  trackEvent('game_completed', {
    opening_name,
    line_name,
    total_moves,
    correct_moves,
    accuracy: total_moves > 0 ? (correct_moves / total_moves) * 100 : 0,
    duration_seconds,
    timestamp: new Date().toISOString()
  });
};

/**
 * Track UI interactions
 * @param {string} element - UI element (button, modal, etc.)
 * @param {string} action - Action type (click, open, close, etc.)
 * @param {string} label - Additional context
 */
export const trackUIInteraction = (element, action, label = '') => {
  trackEvent('ui_interaction', {
    element,
    action,
    label,
    timestamp: new Date().toISOString()
  });
};

/**
 * Track form submissions
 * @param {string} form_type - Type of form (add_opening, add_line)
 * @param {boolean} success - Whether submission was successful
 * @param {string} error_message - Error message if failed
 */
export const trackFormSubmission = (form_type, success, error_message = '') => {
  trackEvent('form_submission', {
    form_type,
    success,
    error_message,
    timestamp: new Date().toISOString()
  });
};

/**
 * Track audio events
 * @param {string} sound_type - Type of sound (move, capture, check, etc.)
 * @param {string} action - Action (play, queue, error)
 */
export const trackAudioEvent = (sound_type, action) => {
  trackEvent('audio_event', {
    sound_type,
    action,
    timestamp: new Date().toISOString()
  });
};

/**
 * Track hint usage
 * @param {string} opening_name - Opening name
 * @param {string} line_name - Line name
 * @param {number} move_number - Move number when hint was used
 */
export const trackHintUsage = (opening_name, line_name, move_number) => {
  trackEvent('hint_used', {
    opening_name,
    line_name,
    move_number,
    timestamp: new Date().toISOString()
  });
};

/**
 * Track reset actions
 * @param {string} opening_name - Opening name
 * @param {string} line_name - Line name
 * @param {number} moves_completed - Moves completed before reset
 */
export const trackReset = (opening_name, line_name, moves_completed) => {
  trackEvent('game_reset', {
    opening_name,
    line_name,
    moves_completed,
    timestamp: new Date().toISOString()
  });
};

/**
 * Track navigation events
 * @param {string} action - Navigation action (next_move, previous_move, back_to_courses)
 * @param {string} from_page - Current page
 * @param {string} to_page - Destination page
 */
export const trackNavigation = (action, from_page, to_page = '') => {
  trackEvent('navigation', {
    action,
    from_page,
    to_page,
    timestamp: new Date().toISOString()
  });
};

/**
 * Track errors
 * @param {string} error_type - Type of error
 * @param {string} error_message - Error message
 * @param {string} context - Context where error occurred
 */
export const trackError = (error_type, error_message, context) => {
  trackEvent('error', {
    error_type,
    error_message,
    context,
    timestamp: new Date().toISOString()
  });
};